import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { EnTetePage, Etiquette, Indicateur, Message } from "@cabinet/charte";
import {
  dossiersApi,
  ErreurApi,
  importApi,
  type ApercuVue,
  type CorrespondanceVue,
  type RapportDImportVue,
} from "../api/client";
import {
  affectationDe,
  colonnesDe,
  lettreDeColonne,
  manques,
  proposerAffectation,
  type Affectation,
  type ChampImporte,
} from "../domaine/importation";
import { formaterDate, formaterMontant, formaterNombre } from "../domaine/formats";
import { useSession } from "../session";

type Encodage = "utf-8" | "windows-1252";

interface Fichier {
  nom: string;
  contenu: string;
}

const SEPARATEURS: { valeur: string; libelle: string }[] = [
  { valeur: "", libelle: "Deviné" },
  { valeur: ";", libelle: "Point-virgule" },
  { valeur: ",", libelle: "Virgule" },
  { valeur: "\\t", libelle: "Tabulation" },
  { valeur: "|", libelle: "Barre verticale" },
];

/**
 * Import d'écritures par fichier (EF-0411).
 *
 * Quatre temps, sur un seul écran : choisir le fichier et son encodage ;
 * désigner ce que porte chaque colonne — proposé depuis les en-têtes, ou repris
 * d'une correspondance mémorisée ; contrôler à blanc, avec le rapport des
 * rejets ligne à ligne ; importer les pièces acceptées, en brouillard. Les
 * pièces importées se relisent et se valident ensuite dans le journal, comme
 * les autres : un fichier ne contourne aucun contrôle.
 */
export function PageImport() {
  const { id = "" } = useParams();
  const { peut } = useSession();
  const cache = useQueryClient();

  const [encodage, setEncodage] = useState<Encodage>("utf-8");
  const [separateur, setSeparateur] = useState("");
  const [fichier, setFichier] = useState<Fichier | null>(null);
  const [apercu, setApercu] = useState<ApercuVue | null>(null);
  const [affectation, setAffectation] = useState<Affectation>({});
  const [formatDeDate, setFormatDeDate] = useState("JJ_MM_AAAA");
  const [avecEntete, setAvecEntete] = useState(true);
  const [journalParDefaut, setJournalParDefaut] = useState("");
  const [rapport, setRapport] = useState<RapportDImportVue | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const dossier = useQuery({ queryKey: ["dossier", id], queryFn: () => dossiersApi.consulter(id) });
  const options = useQuery({ queryKey: ["import-options"], queryFn: importApi.options });
  const memorisees = useQuery({
    queryKey: ["import-correspondances", id],
    queryFn: () => importApi.correspondances(id),
  });

  const signaler = (echec: unknown) =>
    setErreur(echec instanceof ErreurApi ? echec.message : "L'opération n'a pas abouti.");

  const correspondance = (): CorrespondanceVue => ({
    colonnes: colonnesDe(affectation),
    formatDeDate,
    avecEntete,
    journalParDefaut: journalParDefaut.trim() || null,
  });

  async function lire(choisi: File, encodageChoisi: Encodage, separateurChoisi: string) {
    setErreur(null);
    setRapport(null);
    const contenu = new TextDecoder(encodageChoisi).decode(await choisi.arrayBuffer());
    setFichier({ nom: choisi.name, contenu });
    try {
      const lu = await importApi.apercu(id, contenu, separateurChoisi || null);
      setApercu(lu);
      if (Object.keys(affectation).length === 0) setAffectation(proposerAffectation(lu.lignes[0] ?? []));
    } catch (echec) {
      signaler(echec);
    }
  }

  async function relire(encodageChoisi: Encodage, separateurChoisi: string) {
    if (fichier == null) return;
    const entree = document.getElementById("fichier-import") as HTMLInputElement | null;
    const choisi = entree?.files?.[0];
    if (choisi != null) await lire(choisi, encodageChoisi, separateurChoisi);
  }

  const controle = useMutation({
    mutationFn: () =>
      importApi.controler(id, {
        contenu: fichier!.contenu,
        separateur: separateur || null,
        correspondance: correspondance(),
        fichier: fichier!.nom,
      }),
    onMutate: () => {
      setErreur(null);
      setRapport(null);
    },
    onSuccess: setRapport,
    onError: signaler,
  });

  const importation = useMutation({
    mutationFn: () =>
      importApi.importer(id, {
        contenu: fichier!.contenu,
        separateur: separateur || null,
        correspondance: correspondance(),
        fichier: fichier!.nom,
      }),
    onMutate: () => setErreur(null),
    onSuccess: (resultat) => {
      setRapport(resultat);
      void cache.invalidateQueries({ queryKey: ["pieces"] });
      void cache.invalidateQueries({ queryKey: ["grand-livre"] });
      void cache.invalidateQueries({ queryKey: ["balance"] });
    },
    onError: signaler,
  });

  const memorisation = useMutation({
    mutationFn: (nom: string) => importApi.memoriser(id, nom, correspondance()),
    onSuccess: () => void cache.invalidateQueries({ queryKey: ["import-correspondances", id] }),
    onError: signaler,
  });

  function appliquer(nom: string) {
    const trouvee = memorisees.data?.find((m) => m.nom === nom);
    if (trouvee == null) return;
    setAffectation(affectationDe(trouvee.correspondance.colonnes));
    setFormatDeDate(trouvee.correspondance.formatDeDate);
    setAvecEntete(trouvee.correspondance.avecEntete);
    setJournalParDefaut(trouvee.correspondance.journalParDefaut ?? "");
    setRapport(null);
  }

  const aCorriger = manques(affectation, journalParDefaut);
  const nombreDeColonnes = apercu?.nombreDeColonnes ?? 0;
  const champs = options.data?.champs ?? [];

  return (
    <>
      <EnTetePage
        surTitre={dossier.data?.raisonSociale ?? "Dossier"}
        titre="Import d'écritures"
        description="Un fichier CSV exporté d'un tableur ou d'un autre logiciel. Chaque pièce passe les contrôles de la grille — journal, période ouverte, imputation, équilibre — et s'enregistre en brouillard ; les rejets se lisent ligne à ligne, avec le numéro de ligne du fichier."
      />

      {erreur != null && <Message type="erreur">{erreur}</Message>}

      <div className="carte import-etape">
        <h2>1. Fichier</h2>
        <div className="champs-en-ligne">
          <label className="champ">
            Fichier CSV
            <input
              id="fichier-import"
              type="file"
              accept=".csv,.txt,.tsv,text/csv,text/plain"
              onChange={(e) => {
                const choisi = e.target.files?.[0];
                setAffectation({});
                if (choisi != null) void lire(choisi, encodage, separateur);
              }}
            />
          </label>
          <label className="champ">
            Encodage
            <select
              value={encodage}
              onChange={(e) => {
                const valeur = e.target.value as Encodage;
                setEncodage(valeur);
                void relire(valeur, separateur);
              }}
            >
              <option value="utf-8">UTF-8</option>
              <option value="windows-1252">Windows (ANSI, 1252)</option>
            </select>
          </label>
          <label className="champ">
            Séparateur
            <select
              value={separateur}
              onChange={(e) => {
                setSeparateur(e.target.value);
                void relire(encodage, e.target.value);
              }}
            >
              {SEPARATEURS.map((s) => (
                <option key={s.valeur} value={s.valeur}>
                  {s.libelle}
                </option>
              ))}
            </select>
          </label>
          {apercu != null && (
            <span className="discret-texte">
              {formaterNombre(apercu.nombreDeLignes)} lignes · {apercu.nombreDeColonnes} colonnes · séparateur «{" "}
              {apercu.separateur === "\t" ? "tabulation" : apercu.separateur} »
            </span>
          )}
        </div>
      </div>

      {apercu != null && (
        <div className="carte import-etape">
          <h2>2. Colonnes</h2>
          <div className="champs-en-ligne">
            {(memorisees.data?.length ?? 0) > 0 && (
              <label className="champ">
                Correspondance mémorisée
                <select defaultValue="" onChange={(e) => appliquer(e.target.value)}>
                  <option value="">—</option>
                  {memorisees.data!.map((m) => (
                    <option key={m.nom} value={m.nom}>
                      {m.nom}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label className="champ">
              Format des dates
              <select value={formatDeDate} onChange={(e) => setFormatDeDate(e.target.value)}>
                {(options.data?.formatsDeDate ?? []).map((f) => (
                  <option key={f.code} value={f.code}>
                    {f.libelle}
                  </option>
                ))}
              </select>
            </label>
            <label className="champ">
              Journal par défaut
              <input
                value={journalParDefaut}
                onChange={(e) => setJournalParDefaut(e.target.value.toUpperCase())}
                placeholder="OD"
                size={6}
              />
            </label>
            <label className="discret-texte">
              <input type="checkbox" checked={avecEntete} onChange={(e) => setAvecEntete(e.target.checked)} /> Première
              ligne d'en-têtes
            </label>
          </div>

          <div className="tableau-conteneur apercu-import">
            <table>
              <thead>
                <tr>
                  <th className="nombre">Ligne</th>
                  {Array.from({ length: nombreDeColonnes }, (_, rang) => (
                    <th key={rang}>
                      <div className="discret-texte">{lettreDeColonne(rang)}</div>
                      <select
                        value={affectation[rang] ?? ""}
                        aria-label={`Champ de la colonne ${lettreDeColonne(rang)}`}
                        className={affectation[rang] != null ? "affectee" : undefined}
                        onChange={(e) => {
                          const champ = (e.target.value || undefined) as ChampImporte | undefined;
                          setRapport(null);
                          setAffectation((courante) => {
                            const suivante: Affectation = {};
                            // Un champ ne se porte que par une colonne : le
                            // donner à celle-ci le retire à l'autre.
                            for (const [r, c] of Object.entries(courante)) {
                              if (c != null && c !== champ) suivante[Number(r)] = c;
                            }
                            suivante[rang] = champ;
                            return suivante;
                          });
                        }}
                      >
                        <option value="">— ignorée —</option>
                        {champs.map((c) => (
                          <option key={c.code} value={c.code}>
                            {c.libelle}
                          </option>
                        ))}
                      </select>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {apercu.lignes.map((ligne, index) => (
                  <tr key={index} className={avecEntete && index === 0 ? "entete-fichier" : undefined}>
                    <td className="nombre discret-texte">{index + 1}</td>
                    {Array.from({ length: nombreDeColonnes }, (_, rang) => (
                      <td key={rang}>{ligne[rang] ?? ""}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {aCorriger.length > 0 ? (
            <p className="discret-texte">Reste à désigner : {aCorriger.join(" ; ")}.</p>
          ) : (
            <div className="barre-actions">
              <button
                type="button"
                className="principal"
                disabled={controle.isPending || !peut("SAISIR_ECRITURE")}
                onClick={() => controle.mutate()}
              >
                Contrôler
              </button>
              <button
                type="button"
                className="tertiaire"
                disabled={memorisation.isPending}
                onClick={() => {
                  const nom = window.prompt("Mémoriser cette correspondance sous le nom :", "");
                  if (nom != null && nom.trim() !== "") memorisation.mutate(nom.trim());
                }}
              >
                Mémoriser la correspondance
              </button>
              {memorisation.isSuccess && <span className="discret-texte">Mémorisée.</span>}
            </div>
          )}
        </div>
      )}

      {rapport != null && (
        <div className="carte import-etape">
          <h2>3. {rapport.enregistre ? "Import effectué" : "Contrôle"}</h2>
          <div className="indicateurs">
            <Indicateur libelle="Lignes lues" valeur={formaterNombre(rapport.lignesLues)} />
            <Indicateur
              libelle={rapport.enregistre ? "Pièces importées" : "Pièces acceptées"}
              valeur={formaterNombre(rapport.piecesAcceptees)}
            />
            <Indicateur libelle="Pièces rejetées" valeur={formaterNombre(rapport.piecesRejetees)} />
            <Indicateur libelle="Lignes rejetées" valeur={formaterNombre(rapport.lignesRejetees)} />
          </div>

          {rapport.enregistre ? (
            <Message type="succes">
              {formaterNombre(rapport.piecesAcceptees)} pièce(s) enregistrée(s) en brouillard.{" "}
              <Link to={`/dossiers/${id}/ecritures?statut=BROUILLARD`}>Les relire et les valider dans le journal</Link>.
            </Message>
          ) : (
            rapport.piecesAcceptees > 0 && (
              <div className="barre-actions">
                <button
                  type="button"
                  className="principal"
                  disabled={importation.isPending}
                  onClick={() => importation.mutate()}
                >
                  Importer {formaterNombre(rapport.piecesAcceptees)} pièce(s) en brouillard
                </button>
                {rapport.piecesRejetees > 0 && (
                  <span className="discret-texte">
                    Les {formaterNombre(rapport.piecesRejetees)} pièce(s) rejetée(s) restent à corriger dans le fichier.
                  </span>
                )}
              </div>
            )
          )}

          {rapport.rejets.length > 0 && (
            <>
              <h3>Rejets, ligne à ligne</h3>
              <div className="tableau-conteneur">
                <table>
                  <thead>
                    <tr>
                      <th className="nombre">Ligne</th>
                      <th>Motif</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rapport.rejets.map((rejet, index) => (
                      <tr key={`${rejet.ligne}-${index}`}>
                        <td className="nombre">{rejet.ligne}</td>
                        <td>{rejet.motif}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <h3>Pièces</h3>
          <div className="tableau-conteneur">
            <table>
              <thead>
                <tr>
                  <th>Pièce</th>
                  <th>Journal</th>
                  <th>Date</th>
                  <th>Libellé</th>
                  <th>Lignes</th>
                  <th className="nombre">Total</th>
                  <th>Verdict</th>
                </tr>
              </thead>
              <tbody>
                {rapport.pieces.map((piece) => (
                  <tr key={piece.cle}>
                    <td>{piece.cle}</td>
                    <td>{piece.journal}</td>
                    <td>{formaterDate(piece.date)}</td>
                    <td>{piece.libelle}</td>
                    <td className="discret-texte">
                      {piece.lignes.length > 1
                        ? `${piece.lignes[0]}–${piece.lignes[piece.lignes.length - 1]}`
                        : piece.lignes[0]}
                    </td>
                    <td className="nombre">{piece.total != null ? formaterMontant(piece.total) : "—"}</td>
                    <td>
                      {piece.acceptee ? (
                        piece.piece != null ? (
                          <Link to={`/dossiers/${id}/ecritures?piece=${piece.piece}`}>
                            <Etiquette variante="succes">Importée</Etiquette>
                          </Link>
                        ) : (
                          <Etiquette variante="succes">Acceptée</Etiquette>
                        )
                      ) : (
                        <>
                          <Etiquette variante="bloquant">Rejetée</Etiquette>{" "}
                          <span className="discret-texte">{piece.motif}</span>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
